/**
 * Iconos propios de la landing.
 *
 * Dibujados a mano para ESTE producto en vez de tomados de una librería: un
 * set genérico haría que ÁGORA se viera como cualquier otra plataforma, y
 * justo lo que hay que comunicar es que acá la evidencia tiene autor. Cada
 * uno usa el degradado de marca sobre trazo, con la geometría del símbolo que
 * nombra —una mano que aporta, una onda de voz, una cita que tira de un hilo—.
 *
 * Todos comparten viewBox 48×48, trazo de 2 y `currentColor` como respaldo,
 * así heredan tamaño y color del contenedor.
 */

type Props = { className?: string; gradiente?: string };

function Defs({ id }: { id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="var(--color-primary-600)" />
        <stop offset="55%" stopColor="var(--color-cyan-500)" />
        <stop offset="100%" stopColor="var(--color-violet-500)" />
      </linearGradient>
    </defs>
  );
}

const base = 'h-full w-full';

/** Bolsillo: un teléfono del que sale una mano que aporta. */
export function IconoAporte({ className = base, gradiente = 'g-aporte' }: Props) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <Defs id={gradiente} />
      <rect x="9" y="6" width="22" height="36" rx="4" stroke={`url(#${gradiente})`} strokeWidth="2.2" />
      <path d="M15 13h10M15 19h7" stroke={`url(#${gradiente})`} strokeWidth="2.2" strokeLinecap="round" />
      {/* la mano que levanta el aporte */}
      <path
        d="M27 33c0-4 3-7 7-7h5c1.7 0 3 1.3 3 3s-1.3 3-3 3h-4"
        stroke={`url(#${gradiente})`} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      />
      <circle cx="35" cy="18" r="3.5" stroke={`url(#${gradiente})`} strokeWidth="2.2" />
    </svg>
  );
}

/** Voz: onda de audio que se vuelve línea de texto. */
export function IconoVoz({ className = base, gradiente = 'g-voz' }: Props) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <Defs id={gradiente} />
      {[
        { x: 6, h: 10 },
        { x: 12, h: 20 },
        { x: 18, h: 30 },
        { x: 24, h: 16 },
      ].map((b) => (
        <path
          key={b.x}
          d={`M${b.x} ${24 - b.h / 2}v${b.h}`}
          stroke={`url(#${gradiente})`}
          strokeWidth="2.6"
          strokeLinecap="round"
        />
      ))}
      {/* la onda se convierte en renglones: la transcripción */}
      <path d="M32 18h12M32 24h10M32 30h12" stroke={`url(#${gradiente})`} strokeWidth="2.2" strokeLinecap="round" opacity=".85" />
    </svg>
  );
}

/** Citable: una comilla que tira de un hilo hasta la persona que lo dijo. */
export function IconoCita({ className = base, gradiente = 'g-cita' }: Props) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <Defs id={gradiente} />
      <path
        d="M8 20c0-4 3-7 7-7v4c-2 0-3 1.4-3 3h3v9H8v-9zM20 20c0-4 3-7 7-7v4c-2 0-3 1.4-3 3h3v9h-7v-9z"
        stroke={`url(#${gradiente})`} strokeWidth="2" strokeLinejoin="round"
      />
      {/* el hilo hacia el autor */}
      <path d="M30 32c6 0 6 6 11 6" stroke={`url(#${gradiente})`} strokeWidth="2" strokeLinecap="round" strokeDasharray="3 3" />
      <circle cx="41" cy="38" r="4" fill={`url(#${gradiente})`} />
    </svg>
  );
}

/** Validación asíncrona: documento con marca de revisado y reloj. */
export function IconoValidar({ className = base, gradiente = 'g-validar' }: Props) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <Defs id={gradiente} />
      <path
        d="M12 6h16l8 8v20a4 4 0 0 1-4 4H12a4 4 0 0 1-4-4V10a4 4 0 0 1 4-4z"
        stroke={`url(#${gradiente})`} strokeWidth="2.2" strokeLinejoin="round"
      />
      <path d="M28 6v8h8" stroke={`url(#${gradiente})`} strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M15 26l4 4 9-9" stroke={`url(#${gradiente})`} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="37" cy="35" r="7" fill="var(--surface-raised)" stroke={`url(#${gradiente})`} strokeWidth="2" />
      <path d="M37 32v3.2l2 1.4" stroke={`url(#${gradiente})`} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/* ── Las cinco familias del método ────────────────────────────────────────── */

/** Empatizar: un ojo que escucha (iris que es también onda). */
export function IconoEmpatizar({ className = base, color = 'currentColor' }: Props & { color?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <path d="M4 24s7-11 20-11 20 11 20 11-7 11-20 11S4 24 4 24z" stroke={color} strokeWidth="2.2" strokeLinejoin="round" />
      <circle cx="24" cy="24" r="6" stroke={color} strokeWidth="2.2" />
      <path d="M21 24h6" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

/** Definir: un embudo que ordena lo disperso en una decisión. */
export function IconoDefinir({ className = base, color = 'currentColor' }: Props & { color?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <path d="M7 9h34L28 25v13l-8 5V25L7 9z" stroke={color} strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M14 15h20" stroke={color} strokeWidth="2" strokeLinecap="round" opacity=".6" />
    </svg>
  );
}

/** Idear: una chispa que se ramifica en varias opciones. */
export function IconoIdear({ className = base, color = 'currentColor' }: Props & { color?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <circle cx="24" cy="14" r="7" stroke={color} strokeWidth="2.2" />
      <path d="M24 21v6M24 27l-10 8M24 27l10 8M24 27v10" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="13" cy="37" r="3" fill={color} />
      <circle cx="24" cy="39" r="3" fill={color} />
      <circle cx="35" cy="37" r="3" fill={color} />
    </svg>
  );
}

/** Prototipar: piezas que se arman antes de construir en serio. */
export function IconoPrototipar({ className = base, color = 'currentColor' }: Props & { color?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <rect x="6" y="6" width="16" height="16" rx="3" stroke={color} strokeWidth="2.2" />
      <rect x="26" y="6" width="16" height="16" rx="3" stroke={color} strokeWidth="2.2" strokeDasharray="4 3" />
      <rect x="6" y="26" width="16" height="16" rx="3" stroke={color} strokeWidth="2.2" strokeDasharray="4 3" />
      <path d="M28 34h12M34 28v12" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

/** Testear: una lupa sobre una marca de verificación. */
export function IconoTestear({ className = base, color = 'currentColor' }: Props & { color?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <circle cx="21" cy="21" r="14" stroke={color} strokeWidth="2.2" />
      <path d="M31 31l10 10" stroke={color} strokeWidth="2.6" strokeLinecap="round" />
      <path d="M15 21l4 4 8-8" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export const ICONOS_FAMILIA: Record<string, (p: Props & { color?: string }) => JSX.Element> = {
  Empatizar: IconoEmpatizar,
  Definir: IconoDefinir,
  Idear: IconoIdear,
  Prototipar: IconoPrototipar,
  Testear: IconoTestear,
};

/** Cada familia con su color: el método se reconoce por tono, no por leerlo. */
export const COLOR_FAMILIA: Record<string, string> = {
  Empatizar: 'var(--color-cyan-500)',
  Definir: 'var(--color-primary-600)',
  Idear: 'var(--color-violet-500)',
  Prototipar: 'var(--color-recognition)',
  Testear: 'var(--color-success)',
};
